import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import permissionModuleTree from '../../data/permissionModuleTree';

const CRUD_COLUMNS = [
  { key: 'view', label: 'View / Read' },
  { key: 'create', label: 'Create' },
  { key: 'update', label: 'Update' },
  { key: 'delete', label: 'Delete' },
];

const FITUR_TAMBAHAN = [
  { key: 'exportData', label: 'Export Data' },
  { key: 'importData', label: 'Import Data' },
  { key: 'print', label: 'Print' },
  { key: 'sendEmail', label: 'Send Email' },
  { key: 'approve', label: 'Approve' },
  { key: 'cancelReject', label: 'Cancel / Reject' },
  { key: 'duplicate', label: 'Duplicate' },
  { key: 'convert', label: 'Convert' },
];

const DETAIL_TABS = [
  { key: 'permissions', label: 'Permissions' },
  { key: 'dataScope', label: 'Data Scope' },
  { key: 'fiturTambahan', label: 'Fitur Tambahan' },
];

const AVATAR_COLORS = ['#6366f1', '#f97316', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e'];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const getAvatarColor = (name) => {
  const sum = (name || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const countLeaves = (nodes) => {
  let count = 0;
  nodes.forEach((node) => {
    if (node.children && node.children.length > 0) {
      count += countLeaves(node.children);
    } else {
      count += 1;
    }
  });
  return count;
};

const countTopLevelModules = (nodes) => nodes.length;

const JobTitlePermission = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [jobTitles, setJobTitles] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);

  const [activeTab, setActiveTab] = useState('permissions');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [permissionState, setPermissionState] = useState({});
  const [dataScope, setDataScope] = useState('department'); // own | department | global
  const [fiturTambahan, setFiturTambahan] = useState({});
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null); // { type: 'success' | 'error', text }

  const selectedJobTitle = useMemo(
    () => jobTitles.find((jt) => String(jt.id) === String(id)) || null,
    [jobTitles, id]
  );

  useEffect(() => {
    const fetchJobTitles = async () => {
      setIsLoadingList(true);
      try {
        const response = await api.get('/md/job-title-page', { params: { page: 0, size: 200, search: '' } });
        setJobTitles(response.data.content || []);
      } catch (error) {
        setJobTitles([]);
      } finally {
        setIsLoadingList(false);
      }
    };
    fetchJobTitles();
  }, []);

  const loadPermissions = async () => {
    if (!id) return;
    setIsLoadingPermissions(true);
    setSaveMessage(null);
    try {
      const response = await api.get(`/jobtitle-permissions/${id}`);
      const data = response.data || {};
      setPermissionState(data.modules || {});
      setDataScope(data.dataScope || 'department');
      setFiturTambahan(data.fiturTambahan || {});
    } catch (error) {
      setPermissionState({});
      setDataScope('department');
      setFiturTambahan({});
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  useEffect(() => {
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    if (!id) return;
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await api.put(`/jobtitle-permissions/${id}`, {
        modules: permissionState,
        dataScope,
        fiturTambahan,
      });
      setSaveMessage({ type: 'success', text: 'Perubahan berhasil disimpan.' });
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Gagal menyimpan perubahan. Silakan coba lagi.' });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleGroupCollapsed = (key) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => setCollapsedGroups({});
  const collapseAll = () => {
    const allGroupKeys = {};
    const walk = (nodes) => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          allGroupKeys[node.key] = true;
          walk(node.children);
        }
      });
    };
    walk(permissionModuleTree);
    setCollapsedGroups(allGroupKeys);
  };

  const toggleCapability = (nodeKey, columnKey) => {
    setPermissionState((prev) => ({
      ...prev,
      [nodeKey]: {
        ...prev[nodeKey],
        [columnKey]: !prev[nodeKey]?.[columnKey],
      },
    }));
  };

  const toggleFiturTambahan = (key) => {
    setFiturTambahan((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const totalPermissionCount = useMemo(() => {
    let count = 0;
    Object.values(permissionState).forEach((cols) => {
      Object.values(cols || {}).forEach((checked) => {
        if (checked) count += 1;
      });
    });
    Object.values(fiturTambahan).forEach((checked) => {
      if (checked) count += 1;
    });
    return count;
  }, [permissionState, fiturTambahan]);

  const renderTreeNode = (node, depth) => {
    const isGroup = node.children && node.children.length > 0;

    if (isGroup) {
      const isCollapsed = Boolean(collapsedGroups[node.key]);
      return (
        <React.Fragment key={node.key}>
          <div
            className="d-flex align-items-center"
            style={{
              cursor: 'pointer',
              paddingLeft: depth === 0 ? 0 : depth * 20,
              paddingBlock: depth === 0 ? 8 : 6,
              fontWeight: depth === 0 ? 700 : 600,
              fontSize: depth === 0 ? '1rem' : '0.9rem',
              color: depth === 0 ? 'var(--tblr-body-color, #1d273b)' : 'var(--tblr-secondary, #5c6b7d)',
            }}
            onClick={() => toggleGroupCollapsed(node.key)}
          >
            <span className="me-2" style={{ display: 'inline-block', width: 12 }}>
              {isCollapsed ? '▸' : '▾'}
            </span>
            {node.label}
          </div>
          {!isCollapsed && (
            <div style={depth === 0 ? { paddingLeft: 16, borderLeft: '2px solid var(--tblr-border-color, #dce1e7)' } : undefined}>
              {node.children.map((child) => renderTreeNode(child, depth + 1))}
            </div>
          )}
        </React.Fragment>
      );
    }

    return (
      <div
        key={node.key}
        className="d-flex align-items-center flex-wrap py-2 border-bottom"
        style={{ paddingLeft: depth * 20, gap: 24 }}
      >
        <div style={{ minWidth: 200 }}>{node.label}</div>
        <div className="d-flex flex-wrap" style={{ gap: 20 }}>
          {CRUD_COLUMNS.map((column) => (
            <label key={column.key} className="form-check d-flex align-items-center gap-2 mb-0" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={Boolean(permissionState[node.key]?.[column.key])}
                onChange={() => toggleCapability(node.key, column.key)}
              />
              <span className="form-check-label">{column.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderPermissionsTab = () => (
    <div className="row g-3">
      <div className="col-lg-8">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <div className="fw-bold">Module & Permission</div>
            <div className="text-secondary small">Atur hak akses untuk setiap module dan fitur.</div>
          </div>
          <div className="d-flex gap-3">
            <a href="#" className="link-success" onClick={(e) => { e.preventDefault(); expandAll(); }}>Expand All</a>
            <a href="#" className="link-secondary" onClick={(e) => { e.preventDefault(); collapseAll(); }}>Collapse All</a>
          </div>
        </div>
        <div>{permissionModuleTree.map((node) => renderTreeNode(node, 0))}</div>
      </div>

      <div className="col-lg-4">
        <div className="card">
          <div className="card-body">
            <div className="fw-bold mb-2">Ringkasan</div>
            <div className="d-flex justify-content-between py-1">
              <span className="text-secondary">Status Role</span>
              <span className="badge bg-success-lt text-success">Active</span>
            </div>
            <div className="d-flex justify-content-between py-1">
              <span className="text-secondary">Total Module</span>
              <span>{countTopLevelModules(permissionModuleTree)} Module</span>
            </div>
            <div className="d-flex justify-content-between py-1">
              <span className="text-secondary">Total Permission</span>
              <span>{totalPermissionCount} / {countLeaves(permissionModuleTree) * CRUD_COLUMNS.length + FITUR_TAMBAHAN.length} Permission</span>
            </div>
            <div className="d-flex justify-content-between py-1">
              <span className="text-secondary">Assigned Users</span>
              <span>{selectedJobTitle?.userCount ?? 0}</span>
            </div>
            <div className="d-flex justify-content-between py-1">
              <span className="text-secondary">Terakhir Diubah</span>
              <span>-</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDataScopeTab = () => (
    <div className="row g-3">
      <div className="col-lg-6">
        <div className="card">
          <div className="card-body">
            <div className="fw-bold">Data Scope / Visibility</div>
            <div className="text-secondary small mb-3">Tentukan jangkauan data yang dapat diakses.</div>
            {[
              { key: 'own', label: 'View Own', desc: 'Hanya data yang dibuat oleh sendiri' },
              { key: 'department', label: 'View Department', desc: 'Data dalam departemen yang sama' },
              { key: 'global', label: 'View Global', desc: 'Semua data di seluruh perusahaan' },
            ].map((option) => (
              <label key={option.key} className="d-flex align-items-start gap-2 mb-2" style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  className="form-check-input mt-1"
                  name="dataScope"
                  checked={dataScope === option.key}
                  onChange={() => setDataScope(option.key)}
                />
                <div>
                  <div className="fw-500">{option.label}</div>
                  <div className="text-secondary small">{option.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderFiturTambahanTab = () => (
    <div className="row g-3">
      <div className="col-lg-6">
        <div className="card">
          <div className="card-body">
            <div className="fw-bold">Fitur Tambahan</div>
            <div className="text-secondary small mb-3">Hak akses fitur khusus untuk role ini.</div>
            <div className="row g-2">
              {FITUR_TAMBAHAN.map((feature) => (
                <div className="col-6" key={feature.key}>
                  <label className="form-check d-flex align-items-center gap-2 mb-0" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={Boolean(fiturTambahan[feature.key])}
                      onChange={() => toggleFiturTambahan(feature.key)}
                    />
                    <span className="form-check-label">{feature.label}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    if (activeTab === 'dataScope') return renderDataScopeTab();
    if (activeTab === 'fiturTambahan') return renderFiturTambahanTab();
    return renderPermissionsTab();
  };

  return (
    <>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="text-secondary small mb-1">
                <a href="#" className="link-secondary" onClick={(e) => { e.preventDefault(); navigate('/masterdata-jobtitle'); }}>Job Title</a> / Hak Akses
              </div>
              <h2 className="page-title">Job Title — Hak Akses</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            {!selectedJobTitle ? (
              <div className="card-body text-secondary text-center py-5">
                {isLoadingList ? 'Loading...' : 'Job title not found.'}
              </div>
            ) : (
              <>
                <div className="card-body d-flex align-items-start gap-3 border-bottom">
                  <span
                    className="avatar rounded-circle text-white d-flex align-items-center justify-content-center"
                    style={{ background: getAvatarColor(selectedJobTitle.name), width: 56, height: 56, fontSize: 20, flexShrink: 0 }}
                  >
                    {getInitials(selectedJobTitle.name)}
                  </span>
                  <div className="flex-fill">
                    <div className="d-flex align-items-center gap-2">
                      <h3 className="mb-0">{selectedJobTitle.name}</h3>
                      <span className="badge bg-success-lt text-success">Active</span>
                    </div>
                    <div className="text-secondary">Mengatur hak akses menu untuk job title ini.</div>
                    <div className="d-flex flex-wrap gap-4 mt-2 text-secondary small">
                      <span>Department: <strong className="text-body">{selectedJobTitle.departementName || '-'}</strong></span>
                      <span>Created By: <strong className="text-body">{selectedJobTitle.cusername || 'System'}</strong></span>
                      <span>Created At: <strong className="text-body">{selectedJobTitle.cdate ? new Date(selectedJobTitle.cdate).toLocaleDateString() : '-'}</strong></span>
                      <span>Users: <strong className="text-body">{selectedJobTitle.userCount ?? 0}</strong></span>
                    </div>
                  </div>
                </div>

                <div className="card-header border-bottom-0">
                  <ul className="nav nav-tabs card-header-tabs" role="tablist">
                    {DETAIL_TABS.map((tab) => (
                      <li className="nav-item-tab" role="presentation" key={tab.key}>
                        <a
                          href={`#tabs-${tab.key}`}
                          className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                          role="tab"
                          onClick={(e) => { e.preventDefault(); setActiveTab(tab.key); }}
                        >
                          {tab.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-body">
                  {saveMessage && (
                    <div className={`alert ${saveMessage.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                      {saveMessage.text}
                    </div>
                  )}
                  {isLoadingPermissions ? (
                    <div className="text-secondary py-5 text-center">Loading permissions...</div>
                  ) : renderTabContent()}
                </div>

                <div className="card-footer d-flex gap-2">
                  <a
                    href="#"
                    className="btn btn-link link-secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      loadPermissions();
                    }}
                  >
                    Reset
                  </a>
                  <button
                    type="button"
                    className="btn btn-primary ms-auto"
                    disabled={isSaving || isLoadingPermissions}
                    onClick={handleSave}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default JobTitlePermission;
